import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AdminManagement = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    role: 'editor'
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/admins');
      setAdmins(response.data.data || []);
    } catch (error) {
      console.error('Error loading admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError('');

    try {
      const response = await axios.post('/auth/admin/create', createForm);
      
      // Add new admin to list
      setAdmins([...admins, response.data.data.admin]);
      
      // Reset form and close modal
      setCreateForm({
        fullName: '',
        username: '',
        email: '',
        password: '',
        role: 'editor'
      });
      setShowCreateModal(false);
    } catch (error) {
      setCreateError(error.response?.data?.message || 'خطأ في إنشاء الحساب');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggleAdminStatus = async (adminId, currentStatus) => {
    try {
      await axios.patch(`/admin/admins/${adminId}/status`, { isActive: !currentStatus });
      
      // Update local state
      setAdmins(admins.map(admin => 
        admin._id === adminId ? { ...admin, isActive: !currentStatus } : admin
      ));
    } catch (error) {
      console.error('Error updating admin status:', error);
      alert(error.response?.data?.message || 'خطأ في تحديث حالة المشرف');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (adminId === user?.id) {
      alert('لا يمكنك حذف حسابك الخاص');
      return;
    }

    if (window.confirm('هل أنت متأكد من حذف هذا المشرف؟')) {
      try {
        await axios.delete(`/admin/admins/${adminId}`);
        
        // Remove from local state
        setAdmins(admins.filter(admin => admin._id !== adminId));
      } catch (error) {
        console.error('Error deleting admin:', error);
        alert(error.response?.data?.message || 'خطأ في حذف المشرف');
      }
    }
  };

  const RoleBadge = ({ role }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      role === 'admin' 
        ? 'bg-purple-100 text-purple-800' 
        : 'bg-blue-100 text-blue-800'
    }`}>
      {role === 'admin' ? 'مشرف' : 'محرر'}
    </span>
  );

  const StatusBadge = ({ isActive }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isActive 
        ? 'bg-green-100 text-green-800' 
        : 'bg-red-100 text-red-800'
    }`}>
      {isActive ? 'نشط' : 'غير نشط'}
    </span>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إدارة المشرفين والمحررين</h1>
          <p className="mt-1 text-sm text-gray-600">
            إدارة حسابات المشرفين والمحررين في النظام
          </p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة مشرف جديد
          </button>
        )}
      </div>

      {/* Admins Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الاسم الكامل
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  اسم المستخدم
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  البريد الإلكتروني
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الدور
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  آخر دخول
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    <div className="animate-pulse">جاري التحميل...</div>
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    لا توجد مشرفين
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center ml-3">
                          <Shield className="w-4 h-4 text-white" />
                        </div>
                        {admin.fullName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {admin.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {admin.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <RoleBadge role={admin.role} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge isActive={admin.isActive} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {admin.lastLogin 
                        ? new Date(admin.lastLogin).toLocaleDateString('ar-EG')
                        : 'لم يدخل بعد'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        {user?.role === 'admin' && admin._id !== user?.id && (
                          <>
                            <button
                              onClick={() => handleToggleAdminStatus(admin._id, admin.isActive)}
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                admin.isActive 
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {admin.isActive ? 'إلغاء التفعيل' : 'تفعيل'}
                            </button>
                            <button
                              onClick={() => handleDeleteAdmin(admin._id)}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                        {admin._id === user?.id && (
                          <span className="text-xs text-gray-500">حسابك الشخصي</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-right w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        إضافة مشرف جديد
                      </h3>
                      <button
                        onClick={() => setShowCreateModal(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>

                    <form onSubmit={handleCreateAdmin} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          الاسم الكامل
                        </label>
                        <input
                          type="text"
                          required
                          value={createForm.fullName}
                          onChange={(e) => setCreateForm({ ...createForm, fullName: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          اسم المستخدم
                        </label>
                        <input
                          type="text"
                          required
                          value={createForm.username}
                          onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          البريد الإلكتروني
                        </label>
                        <input
                          type="email"
                          required
                          value={createForm.email}
                          onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          كلمة المرور
                        </label>
                        <input
                          type="password"
                          required
                          value={createForm.password}
                          onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-right focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          الدور
                        </label>
                        <select
                          value={createForm.role}
                          onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="editor">محرر</option>
                          <option value="admin">مشرف</option>
                        </select>
                      </div>

                      {createError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-600">{createError}</p>
                        </div>
                      )}

                      <div className="flex justify-end space-x-3 space-x-reverse pt-4">
                        <button
                          type="button"
                          onClick={() => setShowCreateModal(false)}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          إلغاء
                        </button>
                        <button
                          type="submit"
                          disabled={createLoading}
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {createLoading ? 'جاري الإنشاء...' : 'إضافة'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;